// =============================================================================
// MobileReports.tsx
// Mobile Reports tab — primary report-type Dropdown + secondary Filter toggle
// + 3-dot overflow menu (Export CSV / Export PDF). Below: filter panel,
// summary card, and the order list (flat or grouped per report type).
// =============================================================================

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Dropdown, EmptyState } from '../ui';

import { formatCurrency, formatDate } from '../../lib/utils';

import { Order, Department, StaffMember } from '../../lib/types';

import { csvRow } from '../reports/csvEscape';

import { S, STATUS_TONE } from './mobileStyles';

// ── TYPES ────────────────────────────────────────────────────────────

interface MobileReportsProps {
  orders: Order[];
  currency: string;
  locale: string;
  departments: Department[];
  staffDirectory: StaffMember[];
  reportType: string;
  setReportType: (v: string) => void;
  setSelectedOrder: (o: Order) => void;
}

// ── CONSTANTS ────────────────────────────────────────────────────────

const REPORT_TYPES = [
  { value: 'all', label: 'All Orders' },
  { value: 'staff', label: 'By Staff' },
  { value: 'vendor', label: 'By Vendor' },
  { value: 'department', label: 'By Department' },
  { value: 'monthly', label: 'Monthly Summary' },
];

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ── HELPERS ──────────────────────────────────────────────────────────

const formatMonth = (ym: string): string => {
  if (!ym || ym === 'Unknown') return ym;
  const [y, m] = ym.split('-');
  return `${MONTH_NAMES[parseInt(m) - 1] || m} ${y}`;
};

// ── MAIN COMPONENT ───────────────────────────────────────────────────

export default function MobileReports({
  orders, currency, locale, departments, staffDirectory,
  reportType, setReportType, setSelectedOrder,
}: MobileReportsProps) {

  const [filterDept, setFilterDept] = useState('all');
  const [filterStaff, setFilterStaff] = useState('');
  const [staffListOpen, setStaffListOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showOverflow, setShowOverflow] = useState(false);
  const overflowTriggerRef = useRef<HTMLButtonElement>(null);
  const overflowPanelRef   = useRef<HTMLDivElement>(null);
  const [overflowPos, setOverflowPos] = useState<{ top: number; right: number } | null>(null);

  // Type-to-search staff matches (same pattern as MobileInventoryIssueSheet).
  const staffMatches = useMemo(() => {
    const q = filterStaff.trim().toLowerCase();
    if (!q || !staffListOpen) return [];
    return staffDirectory
      .filter(s => s.active !== false)
      .filter(s => (s.name || '').toLowerCase().includes(q) || (s.short_name || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [staffDirectory, filterStaff, staffListOpen]);

  // Overflow panel is portaled to document.body to escape the parent card's
  // overflow:hidden clip. Position is recomputed from the trigger's rect on
  // open and on scroll/resize while open.
  useLayoutEffect(() => {
    if (!showOverflow || !overflowTriggerRef.current) return;
    const compute = () => {
      const r = overflowTriggerRef.current!.getBoundingClientRect();
      setOverflowPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    };
    compute();
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [showOverflow]);

  useEffect(() => {
    if (!showOverflow) return;
    const h = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (overflowTriggerRef.current?.contains(t)) return;
      if (overflowPanelRef.current?.contains(t)) return;
      setShowOverflow(false);
    };
    document.addEventListener('mousedown', h);
    document.addEventListener('touchstart', h);
    return () => {
      document.removeEventListener('mousedown', h);
      document.removeEventListener('touchstart', h);
    };
  }, [showOverflow]);

  // Filter orders
  const filtered = useMemo(() => {
    return orders.filter(o => {
      if (filterDept !== 'all' && o.department_id !== filterDept) return false;
      if (filterStaff.trim()) {
        const q = filterStaff.toLowerCase();
        if (!o.staff_name?.toLowerCase().includes(q)) return false;
      }
      if (dateFrom && (o.order_date || '') < dateFrom) return false;
      if (dateTo && (o.order_date || '') > dateTo) return false;
      return true;
    });
  }, [orders, filterDept, filterStaff, dateFrom, dateTo]);

  const totalSpend = filtered.reduce((s, o) => s + (o.total_cost || 0), 0);
  const activeFilters = (filterDept !== 'all' ? 1 : 0) + (filterStaff.trim() ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0);

  // Group orders by report type
  const grouped = useMemo(() => {
    if (reportType === 'all') return null;
    const map: Record<string, Order[]> = {};
    filtered.forEach(o => {
      let key = '';
      if (reportType === 'staff') key = o.staff_name || 'Unassigned';
      else if (reportType === 'vendor') key = o.vendor || 'Unknown';
      else if (reportType === 'department') key = o.department_name || 'Unknown';
      else if (reportType === 'monthly') key = (o.order_date || '').slice(0, 7) || 'Unknown';
      if (!map[key]) map[key] = [];
      map[key].push(o);
    });
    return Object.entries(map).sort((a, b) => {
      if (reportType === 'monthly') return b[0].localeCompare(a[0]);
      return b[1].length - a[1].length;
    });
  }, [filtered, reportType]);

  // CSV export
  const handleCSV = () => {
    const rows = [csvRow(['Date', 'Staff', 'Vendor', 'Ref #', 'Department', 'Category', 'Status', 'Total'])];
    filtered.forEach(o => {
      rows.push(csvRow([o.order_date || '', o.staff_name || '', o.vendor || '', o.order_number || '', o.department_name || '', o.category || '', o.status || '', o.total_cost?.toFixed(2) || '0']));
    });
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `gopal-report-${reportType}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // PDF export (browser print)
  const handlePDF = () => { window.print(); };

  // Order row
  const OrderRow = ({ o }: { o: Order }) => {
    const items = o.order_items ?? [];
    const primary = items[0];
    const tone = STATUS_TONE[o.status || ''] || 'gray';
    return (
      <div onClick={() => setSelectedOrder(o)} className="m-row" style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', borderBottom: '0.5px solid var(--border)', cursor: 'pointer' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 'var(--m-text-body)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{primary?.item_name || 'Order'}</div>
          <div style={{ fontSize: 'var(--m-text-meta)', color: 'var(--text3)', marginTop: 1 }}>{o.vendor || '—'} · {formatDate(o.order_date || '', locale)}</div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>
          <div style={{ fontSize: 'var(--m-text-body)', fontWeight: 600 }}>{o.total_cost ? formatCurrency(o.total_cost, currency) : '—'}</div>
          <span className={`pill-u ${tone}`}>{o.status}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="m-tab-content">
      {/* Top control row — primary picker (Dropdown), secondary Filter toggle,
          overflow menu for export. Replaces the previous green-hamburger +
          coloured-icon export buttons that all read as primary CTAs. */}
      <div style={{ ...S.card, padding: '10px 12px', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ flex: 1 }}>
            <Dropdown
              value={reportType}
              options={REPORT_TYPES}
              onChange={setReportType}
              width="100%"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`filter-chip ${showFilters ? 'is-active' : ''}`}
            aria-label="Toggle filters"
            style={{ position: 'relative', padding: '6px 10px' }}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3 6h18M6 12h12M10 18h4" /></svg>
            <span>Filter</span>
            {activeFilters > 0 && <span style={{ position: 'absolute', top: -4, right: -4, width: 14, height: 14, background: 'var(--red)', borderRadius: 7, fontSize: 9, color: 'white', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{activeFilters}</span>}
          </button>
          <button
            ref={overflowTriggerRef}
            onClick={() => setShowOverflow(v => !v)}
            className="filter-chip"
            aria-label="More actions"
            style={{ padding: '6px 8px' }}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><circle cx="6" cy="12" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="18" cy="12" r="1.5" /></svg>
          </button>
        </div>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div style={{ ...S.card, padding: '10px 12px', marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 'var(--m-text-label)', fontWeight: 600, color: 'var(--text2)' }}>Filters</span>
            <button onClick={() => { setFilterDept('all'); setFilterStaff(''); setDateFrom(''); setDateTo(''); }} style={{ fontSize: 'var(--m-text-label)', color: 'var(--accent)', background: 'none', border: 'none', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>Clear all</button>
          </div>
          <div style={{ marginBottom: 8, position: 'relative' }}>
            <label className="field-label">Staff</label>
            <input
              value={filterStaff}
              onChange={e => { setFilterStaff(e.target.value); setStaffListOpen(true); }}
              onFocus={() => setStaffListOpen(true)}
              onBlur={() => setTimeout(() => setStaffListOpen(false), 200)}
              placeholder="Type staff name…"
              className="input"
            />
            {staffMatches.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0,
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 8, zIndex: 10, maxHeight: 220, overflow: 'auto',
                marginTop: 2,
              }}>
                {staffMatches.map(s => (
                  <button
                    key={s.id}
                    onMouseDown={() => { setFilterStaff(s.name); setStaffListOpen(false); }}
                    style={{
                      display: 'block', width: '100%', minHeight: 44,
                      padding: '10px 12px', fontSize: 'var(--m-text-meta)', border: 'none',
                      background: 'none', textAlign: 'left' as const, cursor: 'pointer',
                      fontFamily: 'inherit', color: 'var(--text)',
                      borderBottom: '0.5px solid var(--border)',
                    }}
                  >{s.name}</button>
                ))}
              </div>
            )}
          </div>
          <div style={{ marginBottom: 8 }}>
            <label className="field-label">Department</label>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <button
                onClick={() => setFilterDept('all')}
                className={`filter-chip ${filterDept === 'all' ? 'is-active' : ''}`}
                style={{ padding: '6px 14px', fontSize: 'var(--m-text-button)', fontWeight: 600 }}
              >All</button>
              {departments.map(d => (
                <button
                  key={d.id}
                  onClick={() => setFilterDept(d.id)}
                  className={`filter-chip ${filterDept === d.id ? 'is-active' : ''}`}
                  style={{ padding: '6px 14px', fontSize: 'var(--m-text-button)', fontWeight: 600 }}
                >{d.name}</button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ flex: 1 }}>
              <label className="field-label">From</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="input" />
            </div>
            <div style={{ flex: 1 }}>
              <label className="field-label">To</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="input" />
            </div>
          </div>
        </div>
      )}

      {/* Summary card */}
      <div style={{ ...S.card, padding: 14, marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>{REPORT_TYPES.find(r => r.value === reportType)?.label}</div>
          <div style={{ fontSize: 'var(--m-text-label)', color: 'var(--text3)' }}>{filtered.length} orders</div>
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--accent)', marginBottom: 2 }}>{formatCurrency(totalSpend, currency)}</div>
        <div style={{ fontSize: 'var(--m-text-label)', color: 'var(--text3)' }}>Total spend</div>
      </div>

      {/* Order list - flat or grouped */}
      {reportType === 'all' ? (
        <div style={S.card}>
          {filtered.length === 0 ? (
            <EmptyState title="No orders match filters" description="Try clearing filters or expanding the date range." />
          ) : filtered.slice(0, 30).map(o => <OrderRow key={o.id} o={o} />)}
        </div>
      ) : (
        grouped && grouped.map(([group, groupOrders]) => {
          const groupSpend = groupOrders.reduce((s, o) => s + (o.total_cost || 0), 0);
          return (
            <div key={group} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 4px' }}>
                <span style={{ fontSize: 'var(--m-text-label)', fontWeight: 600, color: 'var(--text)' }}>{reportType === 'monthly' ? formatMonth(group) : group}</span>
                <span style={{ fontSize: 'var(--m-text-label)', color: 'var(--text3)' }}>{groupOrders.length} orders · {formatCurrency(groupSpend, currency)}</span>
              </div>
              <div style={S.card}>
                {groupOrders.slice(0, 10).map(o => <OrderRow key={o.id} o={o} />)}
                {groupOrders.length > 10 && (
                  <div style={{ textAlign: 'center', padding: '8px', fontSize: 'var(--m-text-label)', color: 'var(--text3)' }}>+{groupOrders.length - 10} more</div>
                )}
              </div>
            </div>
          );
        })
      )}

      {showOverflow && overflowPos && createPortal(
        <div
          ref={overflowPanelRef}
          className="menu"
          style={{
            position: 'fixed',
            top: overflowPos.top,
            right: overflowPos.right,
            minWidth: 180,
            zIndex: 1000,
          }}
        >
          <button className="menu-item" onClick={() => { setShowOverflow(false); handleCSV(); }}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
            Export CSV
          </button>
          <button className="menu-item" onClick={() => { setShowOverflow(false); handlePDF(); }}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6" /></svg>
            Export PDF
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}
